import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { formatDateLocal } from '@/lib/timeFormatUtils';

export interface TimeSlotClosure {
  id: string;
  time_slot_id: string;
  establishment_id: string;
  start_date: string;
  end_date: string;
  reason: string | null;
  customer_message: string | null;
  created_at: string | null;
}

interface UseTimeSlotClosuresOptions {
  establishmentId?: string;
}

export const useTimeSlotClosures = (options: UseTimeSlotClosuresOptions = {}) => {
  const { establishmentId } = options;
  const { toast } = useToast();
  const [closures, setClosures] = useState<TimeSlotClosure[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchClosures = useCallback(async () => {
    if (!establishmentId) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('time_slot_closures' as any)
        .select('*')
        .eq('establishment_id', establishmentId)
        .order('start_date', { ascending: true });

      if (error) throw error;
      setClosures((data || []) as unknown as TimeSlotClosure[]);
    } catch (error) {
      console.error('Erreur chargement fermetures de service:', error);
    } finally {
      setLoading(false);
    }
  }, [establishmentId]);

  const addClosure = async (timeSlotId: string, startDate: string, endDate: string, reason?: string, customerMessage?: string) => {
    if (!establishmentId) return;

    try {
      const { data, error } = await supabase
        .from('time_slot_closures' as any)
        .insert({
          time_slot_id: timeSlotId,
          establishment_id: establishmentId,
          start_date: startDate,
          end_date: endDate,
          reason: reason || null,
          customer_message: customerMessage || null,
        } as any)
        .select()
        .single();

      if (error) throw error;

      setClosures(prev => [...prev, data as unknown as TimeSlotClosure].sort(
        (a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
      ));

      toast({
        title: "Fermeture de service ajoutée",
        description: `Blocage du ${startDate} au ${endDate}`,
      });

      return data;
    } catch (error) {
      console.error('Erreur ajout fermeture de service:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible d'ajouter la fermeture de service",
      });
      throw error;
    }
  };

  const addClosures = async (timeSlotIds: string[], startDate: string, endDate: string, reason?: string, customerMessage?: string, skippedCount = 0) => {
    if (!establishmentId || timeSlotIds.length === 0) return;

    try {
      const { data, error } = await supabase
        .from('time_slot_closures' as any)
        .insert(timeSlotIds.map(timeSlotId => ({
          time_slot_id: timeSlotId,
          establishment_id: establishmentId,
          start_date: startDate,
          end_date: endDate,
          reason: reason || null,
          customer_message: customerMessage || null,
        })) as any)
        .select();

      if (error) throw error;

      const created = (data || []) as unknown as TimeSlotClosure[];
      setClosures(prev => [...prev, ...created].sort(
        (a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
      ));

      toast({
        title: `${created.length} créneaux bloqués`,
        description: skippedCount > 0
          ? `Fermeture du ${startDate} au ${endDate}. ${skippedCount} déjà bloqué${skippedCount > 1 ? 's' : ''}.`
          : `Fermeture du ${startDate} au ${endDate}`,
      });

      return created;
    } catch (error) {
      console.error('Erreur ajout fermetures de service:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de bloquer tous les créneaux sélectionnés",
      });
      throw error;
    }
  };

  const updateClosure = async (id: string, timeSlotId: string, startDate: string, endDate: string, reason?: string, customerMessage?: string) => {
    try {
      const { data, error } = await supabase
        .from('time_slot_closures' as any)
        .update({
          time_slot_id: timeSlotId,
          start_date: startDate,
          end_date: endDate,
          reason: reason || null,
          customer_message: customerMessage || null,
        } as any)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      setClosures(prev =>
        prev.map(c => c.id === id ? (data as unknown as TimeSlotClosure) : c)
          .sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime())
      );

      toast({
        title: "Fermeture de service modifiée",
      });

      return data;
    } catch (error) {
      console.error('Erreur modification fermeture de service:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de modifier la fermeture de service",
      });
      throw error;
    }
  };

  const deleteClosure = async (id: string) => {
    try {
      const { error } = await supabase
        .from('time_slot_closures' as any)
        .delete()
        .eq('id', id);

      if (error) throw error;

      setClosures(prev => prev.filter(c => c.id !== id));

      toast({
        title: "Fermeture de service supprimée",
      });
    } catch (error) {
      console.error('Erreur suppression fermeture de service:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de supprimer la fermeture de service",
      });
      throw error;
    }
  };

  /**
   * Check if a specific time slot is closed on a given date
   */
  const isSlotClosedOnDate = useCallback((timeSlotId: string, date: string): boolean => {
    return closures.some(c =>
      c.time_slot_id === timeSlotId &&
      date >= c.start_date &&
      date <= c.end_date
    );
  }, [closures]);

  /**
   * Get closure reason for a specific slot on a date
   */
  const getSlotClosureReason = useCallback((timeSlotId: string, date: string): string | null => {
    const closure = closures.find(c =>
      c.time_slot_id === timeSlotId &&
      date >= c.start_date &&
      date <= c.end_date
    );
    return closure?.reason || null;
  }, [closures]);

  /**
   * Get customer-facing message for a specific slot closure on a date.
   * Returns the custom customer_message if set, otherwise a default fallback.
   */
  const getSlotClosureMessage = useCallback((timeSlotId: string, date: string): string | null => {
    const closure = closures.find(c =>
      c.time_slot_id === timeSlotId &&
      date >= c.start_date &&
      date <= c.end_date
    );
    if (!closure) return null;
    return closure.customer_message || 'Établissement fermé ce jour';
  }, [closures]);

  /**
   * Get custom customer_message only (no default fallback).
   * Returns null when the field is empty.
   */
  const getCustomSlotClosureMessage = useCallback((timeSlotId: string, date: string): string | null => {
    const closure = closures.find(c =>
      c.time_slot_id === timeSlotId &&
      date >= c.start_date &&
      date <= c.end_date
    );
    return closure?.customer_message || null;
  }, [closures]);

  /**
   * Get active/upcoming closures (end_date >= today)
   */
  const getActiveClosures = useCallback(() => {
    const today = formatDateLocal(new Date());
    return closures.filter(c => c.end_date >= today);
  }, [closures]);

  /**
   * Get past closures (end_date < today)
   */
  const getPastClosures = useCallback(() => {
    const today = formatDateLocal(new Date());
    return closures.filter(c => c.end_date < today);
  }, [closures]);

  /**
   * Get closures for a specific time slot
   */
  const getClosuresForSlot = useCallback((timeSlotId: string) => {
    return closures.filter(c => c.time_slot_id === timeSlotId);
  }, [closures]);

  useEffect(() => {
    fetchClosures();

    if (!establishmentId) return;

    const subscription = supabase
      .channel(`time_slot_closures_${establishmentId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'time_slot_closures',
          filter: `establishment_id=eq.${establishmentId}`,
        },
        () => {
          fetchClosures();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, [establishmentId, fetchClosures]);

  return {
    closures,
    loading,
    addClosure,
    addClosures,
    updateClosure,
    deleteClosure,
    isSlotClosedOnDate,
    getSlotClosureReason,
    getSlotClosureMessage,
    getCustomSlotClosureMessage,
    getActiveClosures,
    getPastClosures,
    getClosuresForSlot,
    refetch: fetchClosures,
  };
};
